import { APP_INITIALIZER, Inject, Injectable, NgModule, inject, provideAppInitializer } from '@angular/core';
import { CommonModule, DOCUMENT, PathLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { provideHighcharts } from 'highcharts-angular';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes, mapToCanActivate } from '@angular/router';
import { NgxsModule } from '@ngxs/store';
import {
    MissingTranslationHandler,
    MissingTranslationHandlerParams,
    TranslateLoader,
    TranslateModule,
    TranslationObject,
} from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';

import { FeatureService } from '@services/features/feature.service';
import { ToastService } from '@services/common/toast.service';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireConfigState } from '@state/questionnaire.config.state';
import { UserState } from '@state/user.state';
import { AdvancedMeasureState } from '@state/advanced-measure.state';
import { AppComponent } from './app.component';
import { AppDispatcherComponent } from './app-dispatcher.component';
import { AppLayoutModule } from './layout/app.layout.module';
import { ConfigService, DCAR_APP_CONFIG } from './app-config.service';
import { AuthenticationService, CognitoAuthenticationService } from './auth/service';
import { AuthInterceptor, JwtInterceptor, ErrorInterceptor, AuthGuard } from './auth/helpers';
import { AuthenticationModule } from './main/pages/authentication/authentication.module';
import { AppLayoutComponent } from './layout/app.layout.component';
import { SolutionState } from '@state/solution.state';
import { ComponentsModule } from './main/apps/components/components.module';
import { DCarbonizeAppConfig } from './@types/shared/app-config';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { environment } from 'environments/environment';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { COOKIE_CONSENT_COOKIE_NAME } from '@services/common/consent.service';
import { LocalizationService } from '@services/app-initialization/localization.service';
import { OrganizationState } from '@state/organization.state';
import { QuestionnaireCsrdConfigState } from '@state/questionnaire.csrd.config.state';
import { QuestionnaireCsrdState } from '@state/questionnaire.csrd.state';
import { EmissionFactorItemsState } from '@state/organizationItems.state';
import { FileUploadState } from '@state/file-upload.state';
import { KpiDenominatorState } from '@state/kpi-denominator.state';
import { NGXS_OPTIONS } from '@state/ngxs-options';
import { applyBrandFont, clientBrand, clientPreset } from './theme/clients';

/** The build's client: its colour drives the theme preset, its typeface the global font. */
const brand = clientBrand(environment.client.name);

const cookieConfig: NgcCookieConsentConfig = {
    cookie: {
        // Version-suffixed: renaming it is how a new notice reaches users who
        // already answered the previous one. See `COOKIE_CONSENT_COOKIE_NAME`.
        name: COOKIE_CONSENT_COOKIE_NAME,
        domain: environment.domain,
    },
    position: 'bottom-right',
    theme: 'classic',
    // Real consent: an explicit Accept/Refuse choice, nothing assumed.
    type: 'opt-in',
    // No `revokable: false` here — the library ignores it and forces the floating
    // revoke tab on for every type other than 'info' (`applyRevokeButton`). It is
    // hidden in styles.scss instead; the choice is changed from the "Cookies"
    // link in the application footer, which reopens this same banner.
};

@Injectable()
export class CustomTranslateLoader implements TranslateLoader {
    constructor(
        private http: HttpClient,
        @Inject(DCAR_APP_CONFIG) private config: DCarbonizeAppConfig,
    ) {}

    getTranslation(lang: string): Observable<TranslationObject> {
        if (this.config.supportedLanguages.includes(lang)) {
            const baseTranslation$ = this.http.get(`./assets/i18n/${lang}.json`);
            if (
                this.config &&
                this.config.client &&
                this.config.client.name &&
                this.config.client.name.toLowerCase() !== 'dcarbonize'
            ) {
                const clientTranslation$ = this.http.get(
                    `./assets/i18n/${this.config.client.name.toLowerCase()}/${lang}.json`,
                );

                return forkJoin([baseTranslation$, clientTranslation$]).pipe(
                    map(([baseTranslation, clientTranslation]) => {
                        return this.mergeTranslations(baseTranslation, clientTranslation);
                    }),
                );
            } else {
                return baseTranslation$;
            }
        } else {
            return of({});
        }
    }

    private mergeTranslations(
        baseTranslation: TranslationObject,
        clientTranslation: TranslationObject,
    ): TranslationObject {
        if (typeof baseTranslation !== 'object' || typeof clientTranslation !== 'object') {
            return clientTranslation;
        }

        const mergedTranslation = { ...baseTranslation };

        for (const key of Object.keys(clientTranslation)) {
            if (
                mergedTranslation.hasOwnProperty(key) &&
                typeof mergedTranslation[key] === 'object' &&
                typeof clientTranslation[key] === 'object'
            ) {
                mergedTranslation[key] = this.mergeTranslations(
                    mergedTranslation[key] as TranslationObject,
                    clientTranslation[key] as TranslationObject,
                );
            } else {
                mergedTranslation[key] = clientTranslation[key] as TranslationObject;
            }
        }

        return mergedTranslation;
    }
}

@Injectable()
export class CustomMissingTranslationHandler implements MissingTranslationHandler {
    constructor(private http: HttpClient) {}

    handle(params: MissingTranslationHandlerParams): Observable<string> {
        if (!environment.production) {
            const translationFallbackPath: string = './assets/i18n/newKeys.json';

            return this.http.get(translationFallbackPath).pipe(
                catchError(() => of(`MISSING TRANSLATION KEY: ${params.key}`)),
                map((missingTranslations) => {
                    const fallback = this.getTranslation(params.key, missingTranslations);
                    // newKeys.json strings can carry {{placeholders}} — interpolate them the same
                    // way a real translation would, instead of leaking the raw braces.
                    return params.translateService.parser.interpolate(fallback, params.interpolateParams) ?? fallback;
                }),
            );
        } else {
            return of(`MISSING TRANSLATION KEY: ${params.key}`);
        }
    }

    private getTranslation(key: string, translations: string | object): string {
        const keys: string[] = key.split('.');
        let result: unknown = translations;

        for (const k of keys) {
            if (typeof result !== 'object' || result === null) {
                return `MISSING TRANSLATION KEY: ${k}`;
            }
            if (!Object.prototype.hasOwnProperty.call(result, k)) {
                return `MISSING TRANSLATION KEY: ${k}`;
            }
            // nosemgrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop
            result = (result as Record<string, unknown>)[k];
            if (!result) {
                return `MISSING TRANSLATION KEY: ${k}`;
            }
        }

        return result as string;
    }
}

const appRoutes: Routes = [
    {
        path: '',
        component: AppLayoutComponent,
        children: [
            { path: '', component: AppDispatcherComponent },
            {
                path: 'app',
                loadChildren: () => import('./main/apps/apps.module').then((m) => m.AppsModule),
                canActivate: mapToCanActivate([AuthGuard]),
            },
            {
                path: 'admin',
                loadChildren: () => import('./main/admin/admin.module').then((m) => m.AdminModule),
                canActivate: mapToCanActivate([AuthGuard]),
            },
            {
                path: 'dedicated_admin',
                loadChildren: () =>
                    import('./main/dedicated_admin/dedicated_admin.module').then((m) => m.DedicatedAdminModule),
                canActivate: mapToCanActivate([AuthGuard]),
            },
        ],
    },
    {
        path: 'auth',
        loadChildren: () =>
            import('./main/pages/authentication/authentication.module').then((m) => m.AuthenticationModule),
    },
    {
        path: 'pages', // to check
        loadChildren: () => import('./main/pages/pages.module').then((m) => m.PagesModule),
    },
    // The Carbon theme style board and its app frame preview: internal pages, left out of production builds.
    ...(environment.production
        ? []
        : [
              {
                  path: 'style-board',
                  loadComponent: () =>
                      import('./theme/style-board/style-board.component').then((m) => m.StyleBoardComponent),
              },
              {
                  path: 'style-board/shell',
                  loadComponent: () =>
                      import('./theme/style-board/shell-preview.component').then((m) => m.ShellPreviewComponent),
              },
          ]),
    {
        path: '**',
        redirectTo: '/pages/not-found',
    },
];

@NgModule({
    declarations: [AppComponent, AppDispatcherComponent],
    bootstrap: [AppComponent],
    imports: [
        CommonModule,
        ComponentsModule,
        BrowserModule,
        AppLayoutModule,
        BrowserAnimationsModule,
        AuthenticationModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoader,
                deps: [HttpClient, DCAR_APP_CONFIG],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: CustomMissingTranslationHandler,
                deps: [HttpClient],
            },
        }),
        NgcCookieConsentModule.forRoot(cookieConfig),
        NgxsModule.forRoot(
            [
                UserState,
                QuestionnaireConfigState,
                QuestionnaireState,
                SolutionState,
                OrganizationState,
                QuestionnaireCsrdConfigState,
                QuestionnaireCsrdState,
                AdvancedMeasureState,
                EmissionFactorItemsState,
                FileUploadState,
                KpiDenominatorState,
            ],
            NGXS_OPTIONS,
        ),
        RouterModule.forRoot(appRoutes, {
            enableTracing: environment.debug?.tracing ?? false,
            scrollPositionRestoration: 'enabled',
        }),
        // PrimeNG modules
        MessageModule,
        ToastModule,
    ],
    providers: [
        LocalizationService,
        { provide: DCAR_APP_CONFIG, useValue: environment },
        {
            provide: APP_INITIALIZER,
            useFactory: (localizationService: LocalizationService) => localizationService.initializeApp(),
            multi: true,
            deps: [LocalizationService],
        },
        provideAppInitializer(() => applyBrandFont(brand.font, inject(DOCUMENT))),
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: ConfigService, useClass: ConfigService, deps: [DCAR_APP_CONFIG] },
        { provide: FeatureService, useClass: FeatureService },
        { provide: AuthenticationService, useClass: CognitoAuthenticationService },
        { provide: MessageService },
        { provide: ToastService },
        provideHttpClient(withInterceptorsFromDi()),
        // Loads highcharts/esm/highcharts on the first chart: the build every chart file imports, so the
        // global options HighchartsInitializationService sets reach the wrapper's charts.
        provideHighcharts(),
        providePrimeNG({
            ripple: true,
            // Dropdown, date picker… panels open on the body: drawn in place, a page, card or dialog
            // that scrolls on its own would cut them off at its edge.
            overlayAppendTo: 'body',
            theme: {
                preset: clientPreset(brand),
                // PrimeNG styles go in the `primeng` cascade layer, so the app's own styles always
                // override them. Same layer order as styles.scss: whichever loads first sets it.
                options: {
                    darkModeSelector: '.app-dark',
                    cssLayer: { name: 'primeng', order: 'carbon-base, primeng' },
                },
            },
        }),
    ],
})
export class AppModule {}
